import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Vector3, Vector2 } from '@babylonjs/core/Maths/math';
import { EasingFunction, ExponentialEase  } from '@babylonjs/core/Animations/easing';
import remove from 'lodash/remove';

import { MeshSystem } from './meshSystem';

export class GravityGrid {

    private system: MeshSystem;

    private starCurve: EasingFunction;

    private blackHoleCurve: EasingFunction;

    private mapDetail = 50;

    private mapSize = 100;

    private gravityDepth = 2;

    private gravityAround = 0.15;

    private halfSize: number;

    private halfDetail: number;

    private step:number;

    private paths: Vector3[][];

    private pathToKeys: string[][];

    private keysToPath = {};

    private center = new Vector2(1, 1);

    constructor(system: MeshSystem) {
        this.system = system;

        this.initMap();
        this.addRibbon();
        
        let frame = true;
        this.system.scene.registerBeforeRender(() => {
            if (frame) {
                try {
                    if (this.paths.length == 0) return;
                    this.ribbon = Mesh.CreateRibbon('', this.paths, false, false, 0, undefined, true, undefined, this.ribbon);
                    // this.ribbon.convertToFlatShadedMesh();
                } catch {
                    console.log('maperror');
                    console.log(this.paths);
                }
            }
            frame = !frame;
        });

        this.starCurve = new ExponentialEase();
        this.starCurve.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);
        this.blackHoleCurve = new ExponentialEase();
        this.blackHoleCurve.setEasingMode(EasingFunction.EASINGMODE_EASEOUT);
    }

    private ribbon: Mesh;

    private addRibbon() {
        this.ribbon = Mesh.CreateRibbon("ribbon", this.paths, false, false, 0, this.system.scene, true, Mesh.BACKSIDE);
        this.ribbon.alwaysSelectAsActiveMesh = true;
        this.ribbon.doNotSyncBoundingInfo = true;
        // this.ribbon.convertToFlatShadedMesh();
        this.ribbon.rotation.x = Math.PI;
        this.ribbon.material = this.system.ribbonMaterial;
    }

    public initMap() {
        this.halfDetail = this.mapDetail / 2;
        this.setCenterAndSize(Vector2.Zero(), 1);
    }
    
    public setCenterAndSize(pos: Vector2, size: number) {
        size = Math.max(size, 2);
        // Whatever the size of the player, the ribbon will get bigger and small to alway take the all screen
        const halfSize = Math.round(size * this.mapSize / 2);
        this.halfSize = Math.round(halfSize / this.mapDetail) * this.mapDetail;
        this.step = Math.round(2 * this.halfSize / this.mapDetail);
        
        const xRound = Math.round(pos.x / this.step) * this.step;
        const yRound = Math.round(-pos.y / this.step) * this.step;
        const newCenter = new Vector2(xRound, -yRound);
        if (this.center.x == newCenter.x && this.center.y == newCenter.y) return;

        const newPaths: Vector3[][] = [];
        const newPathToKeys: string[][] = [];
        for (let x = xRound - this.halfSize; x < xRound + this.halfSize; x += this.step) {
            var linepath: Vector3[] = [];
            var linekey: string[] = [];
            for (let y = yRound - this.halfSize; y < yRound + this.halfSize; y += this.step) {
                linepath.push(new Vector3(x, 0, y));
                linekey.push('');
            }
            newPaths.push(linepath);
            newPathToKeys.push(linekey);
        }
        this.center = newCenter.clone();
        this.paths = newPaths;
        this.pathToKeys = newPathToKeys;
    }

    public setStarPoint(key:string, pos: Vector2, size: number, depth?: number) {
        this.setMassPoint(key, pos, size, this.starCurve, depth);
    }

    public setBlackHolePoint(key: string, pos: Vector2, size: number, depth?: number) {
        this.setMassPoint(key, pos, size, this.blackHoleCurve, depth);
    }

    private setMassPoint(key: string, pos: Vector2, size: number, curve: EasingFunction, depth?: number) {
        this.eraseMass(key);
        const MassToCenter = Vector2.Distance(pos, this.center);
        // If too far from center, ignore it
        if (MassToCenter > this.halfSize) return;
        const massCenter = pos.subtract(this.center);
        const xRound = (Math.round(massCenter.x / this.step)) + this.halfDetail;
        const yRound = (Math.round(-massCenter.y / this.step)) + this.halfDetail;

        size = size/10;
        const alteredPoints: Vector3[] = [];
        if (!depth) depth = size * this.gravityDepth;
        const width = Math.round(size * this.gravityAround * this.mapDetail);
        const newKeysToPath: number[][] = [];

        for (let x = Math.max(xRound - width, 0); x < Math.min(xRound + width, this.mapDetail); x++) {
            for (let y = Math.max(yRound - width, 0); y < Math.min(yRound + width, this.mapDetail); y++) {
                if (!this.paths[x] || !this.paths[x][y]) return;
                const circlePoint = new Vector2(x * this.step - this.halfSize, -(y * this.step - this.halfSize));
                const dist = Vector2.Distance(massCenter, circlePoint);
                const perc = curve.ease(dist / (size * 20));
                const newY = Math.max(depth - depth * perc, 0);

                if (newY > this.paths[x][y].y) {
                    this.paths[x][y].y = newY;
                    alteredPoints.push(this.paths[x][y]);
                    const currentkey = this.pathToKeys[x][y];
                    if (currentkey) {
                        remove(this.keysToPath[currentkey], (xy) => { return xy.x === x && xy.y === y });
                    }
                    this.pathToKeys[x][y] = key;
                    newKeysToPath.push([x, y]);
                }
            }
        }
        this.keysToPath[key] = newKeysToPath;
    }

    public eraseMass(key: string) {
        if (this.keysToPath[key] && this.keysToPath[key].length) {
            for (let i = 0; i < this.keysToPath[key].length; i++) {
                if (!this.keysToPath[key] || !this.keysToPath[key][i]) return;
                const xy = this.keysToPath[key][i];
                if (this.pathToKeys[xy[0]] && this.pathToKeys[xy[0]][xy[1]] == key) {
                    this.paths[xy[0]][xy[1]].y = 0;
                    this.pathToKeys[xy[0]][xy[1]] = '';
                }
            }
            this.keysToPath[key] = [];
        }
    }
}